







































































































































































































































































import {
  Component,
  Vue,
  Watch,
} from 'vue-property-decorator';
import {
  Action,
  Getter,
} from 'vuex-class';
import Table from '@/components/Table.vue';
import IntegrationsEdit from '@/components/IntegrationsEdit.vue';
import { DeviceIntegration, IDataTableHeader, Integration, IntegrationConfiguration, IntegrationOutputType, ManagementIntegrationConfiguration, Project } from '@/types';
import IntegrationsService from '@/services/integrations.service';
import DeviceManagementService from '@/services/deviceManagement.service';
import IntegrationIcon from '@/components/IntegrationIcon.vue';
import Dialog from '@/components/Dialog.vue';
import CancelButton from '@/components/CancelButton.vue';
import ConfirmButton from '@/components/ConfirmButton.vue';

@Component( {
  components: {
    Table,
    IntegrationsEdit,
    IntegrationIcon,
    Dialog,
    CancelButton,
    ConfirmButton,
  },
})
export default class DeviceManagement extends Vue {
  @Getter private projectById: any;
  @Getter private projects: any;
  @Getter private activeOrganisation: any;
  @Getter private hasPermission: any;

  private loading: boolean = true;
  private allIntegrations: Integration[] = [];
  private configuredIntegrationsByProject: Map<number, Array<Partial<ManagementIntegrationConfiguration>>> = new Map();
  private editingIntegrationIndex: number = -1;
  private editedIntegration: Partial<ManagementIntegrationConfiguration> | null = null;
  private bacnetSelected: boolean = false;

  private headers: IDataTableHeader[] = [
    {
      text: 'Name',
      value: 'name',
    },
    {
      text: 'Integrations',
      value: 'integrations',
    },
  ];

  private expandedRows: any[] = [];
  private paginationOptions: number[] = [];

  private readonly severityOptions: object[] = [
    {text: 'info', value: 1},
    {text: 'warning', value: 3},
    {text: 'critical', value: 5},
  ];

  private async mounted() {
    this.loading = true;
    try {
      await Promise.all([this.getAllIntegrations(), this.getDeviceManagementIntegrations()]);
    } finally {
      this.loading = false;
    }
  }

  private async getAllIntegrations() {
    const integrations = await IntegrationsService.getIntegrations();
    this.allIntegrations = integrations.filter((i: Integration) => i.output_type !== IntegrationOutputType.PUBLISHING);
  }

  private async getDeviceManagementIntegrations() {
    const integrations = await DeviceManagementService.getIntegrationsForDeviceManagement();
    this.configuredIntegrationsByProject = new Map();

    integrations.forEach((entry: any) => {
      const key = entry.project_id || -1;
      this.configuredIntegrationsByProject.set(key, this.configuredIntegrationsByProject.has(key) ? [...this.configuredIntegrationsByProject.get(key)!, entry] : [entry]);
    });

  }

  private setExpanded(rows: any) {
    this.editingIntegrationIndex = -1;
    this.expandedRows = rows;
  }

  private addNewIntegration(projectId: number) {
    const newIntegration: Partial<ManagementIntegrationConfiguration> = {
      project_id: projectId === -1 ? null : projectId,
      alerts: true,
      alerts_severity: 1,
      alerts_cleared: false,
      alerts_suppress_repeats: true,
      project_updates: false,
      tasks: false,
      alerts_count_threshold: 1,
    };

    const newArray = [...(this.configuredIntegrationsByProject.get(projectId) || []), newIntegration];
    this.configuredIntegrationsByProject.set(projectId, newArray);
    this.editingIntegrationIndex = newArray.length - 1;
    this.bacnetSelected = false;
    this.$forceUpdate(); // Vue doesn't deep-watch maps
  }

  private editIntegration(index: number) {
    this.editingIntegrationIndex = index;
  }

  private async deleteIntegration(configId: number) {
    if (!configId) {
      const [selected] = this.expandedRows;
      if (!selected || !selected.id) {
        return;
      }

      if (!this.editedIntegration || !this.editedIntegration.id) {
        this.editingIntegrationIndex = -1;
      }
      this.configuredIntegrationsByProject.set(selected.id, (this.configuredIntegrationsByProject.get(selected.id) || []).filter((i) => i.id));
      this.$forceUpdate();
      return;
    }

    this.loading = true;
    try {
      await DeviceManagementService.deleteIntegrationForDeviceManagement(configId);
      await this.getDeviceManagementIntegrations();
    } finally {
      this.loading = false;
    }
  }

  private async save(integration: ManagementIntegrationConfiguration) {
    this.loading = true;

    try {
      const data = {
        alerts_severity: integration.alerts_severity,
        project_id: integration.project_id,
        alerts: integration.alerts,
        alerts_cleared: integration.alerts_cleared,
        alerts_suppress_repeats: integration.alerts_suppress_repeats,
        project_updates: integration.project_updates,
        tasks: integration.tasks,
        alerts_count_threshold: Number(integration.alerts_count_threshold),
      };
      integration.id
        ? await DeviceManagementService.updateDeviceManagementIntegration(integration.id, data)
        : await DeviceManagementService.setDeviceManagementIntegration(integration.integration_id, data);

      this.editingIntegrationIndex = -1;
      await this.getDeviceManagementIntegrations();
    } finally {
      this.loading = false;
    }
  }

  private isBacnet(integration: Integration, integrationConfig: Partial<ManagementIntegrationConfiguration>) {
    this.bacnetSelected = integration.type === 'bacnet';
    if (this.bacnetSelected) {
      Vue.set(integrationConfig, 'alerts', false);
      Vue.set(integrationConfig, 'alerts_severity', 1);
      Vue.set(integrationConfig, 'alerts_suppress_repeats', false);
      Vue.set(integrationConfig, 'alerts_count_threshold', 1);
      Vue.set(integrationConfig, 'alerts_cleared', false);
      Vue.set(integrationConfig, 'project_updates', true);
      Vue.set(integrationConfig, 'tasks', false);
    }
  }

  @Watch('editingIntegrationIndex')
  private handleEditingIntegrationIndexChange(value: number) {
    this.editedIntegration = this.calculateEditedIntegration();
  }

  private get projectsSelectList() {
    return this.projects.map((p: Project) => ({ text: p.name, value: p.id }));
  }

  private get integrationsSelectList() {
    if (!this.expandedRows || this.expandedRows.length === 0) {
      return [];
    }

    const [selectedRow] = this.expandedRows;
    const configs = this.configuredIntegrationsByProject.get(selectedRow.id || -1);

    return this.allIntegrations
      .filter((i: Integration) => selectedRow.id ? true : !(configs || []).find((c: any) => c.integration_id === i.id))
      .map((i: Integration) => ({ text: i.name, icon: i.type, value: i.id }));
  }

  private get canSave() {
    return this.editedIntegration && this.editedIntegration.integration_id && (this.editedIntegration.alerts || this.editedIntegration.alerts_cleared || this.editedIntegration.project_updates || this.editedIntegration.tasks);
  }

  // getter doesn't work, it's bound to a watcher instead
  private calculateEditedIntegration(): Partial<ManagementIntegrationConfiguration> | null {
    if (!this.expandedRows || this.expandedRows.length === 0 || this.editingIntegrationIndex === -1) {
      return null;
    }

    const [selected] = this.expandedRows;
    const configs = this.configuredIntegrationsByProject.get(selected.id) || [];
    return configs[this.editingIntegrationIndex];
  }

  private get rows() {
    return [
      { id: -1, name: `Organisation: ${this.activeOrganisation[0].name}`},
      ...this.projects.map((p: Project) => ({ id: p.id, name: `Project: ${p.name}` })),
    ];
  }

  private get addingIntegration() {
    return this.editedIntegration && !this.editedIntegration.id;
  }

  private get selectedProjectId() {
    if (!this.expandedRows || this.expandedRows.length < 1) {
      return null;
    }

    return this.expandedRows[0].id === -1 ? null : this.expandedRows[0].id;
  }
}
